import axios from "axios";
import { getToken } from "../services/authUtil";

const baseUrl = process.env.REACT_APP_BASE_URL;

const request = async (method, path, data, params) => {
  const token = getToken();
  try {
    const response = await axios({
      method,
      url: `${baseUrl}${path}`,
      data,
      params,
      headers: {
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        "Content-Type": "application/json",
        Accept: "application/json",
      },
    });
    return response.data?.data ?? response.data;
  } catch (error) {
    const payload = error.response?.data;
    const message = payload?.message || payload?.data?.message || error.message || "Request failed";
    // Provider-specific identifiers and credential names are implementation details.
    // Keep them out of property-facing error toasts while preserving the original
    // response in the browser's network/debug tooling for administrators.
    const safeMessage = String(message).replace(/beds24(?:[_-][a-z0-9]+)*/gi, "OTA");
    throw new Error(safeMessage);
  }
};

export const getProperties = () => request("get", "/resort");
export const getUsers = () => request("get", "/user");
export const updateUserProfile = (id, payload) => request("put", `/auth/user/${id}`, payload);
export const getCalendarEntries = (page = 1, perPage = 100, filters = {}) => request("get", "/calendar", null, { page, per_page: perPage, ...filters });
export const generateCalendar = (years, date) => request("get", `/calendar/generate/${Number(years)}/${encodeURIComponent(date)}`);
export const deleteCalendarEntry = (id) => request("delete", `/calendar/${encodeURIComponent(id)}`);

export const getBeds24Connection = (code) => request("get", `/properties/${encodeURIComponent(code)}/beds24`);
export const getBeds24Overview = (code) => request("get", `/properties/${encodeURIComponent(code)}/beds24/overview`);
export const getBeds24Accounts = () => request("get", "/beds24/accounts");
export const connectBeds24 = (code, payload) => request("post", `/properties/${encodeURIComponent(code)}/beds24`, payload);
export const testBeds24Connection = (code) => request("get", `/properties/${encodeURIComponent(code)}/beds24/test`);
export const refreshBeds24Token = (code) => request("post", `/properties/${encodeURIComponent(code)}/beds24/token/refresh`);
export const updateBeds24Status = (code, status) => request("patch", `/properties/${encodeURIComponent(code)}/beds24`, { status });
export const syncBeds24 = (code, from, to) => request("post", `/properties/${encodeURIComponent(code)}/beds24/sync`, { from, to });
export const syncBeds24Bookings = (code, from, to) => request("post", `/properties/${encodeURIComponent(code)}/beds24/bookings/sync`, { from, to });
export const getBeds24UnmatchedEvents = (code, global = false) => request(
  "get",
  global ? "/beds24/unmatched-property-events" : `/properties/${encodeURIComponent(code)}/beds24/unmatched-events`,
  null,
  { per_page: 50 },
);
export const mapBeds24Room = (code, roomType, beds24RoomId, totalInventory) => request(
  "put",
  `/properties/${encodeURIComponent(code)}/beds24/rooms/${roomType}/map`,
  { beds24_room_id: Number(beds24RoomId), total_inventory: totalInventory ? Number(totalInventory) : null }
);

export const getParityRules = (code) => request("get", `/properties/${encodeURIComponent(code)}/price-parity/rules`);
export const createParityRule = (code, payload) => request("post", `/properties/${encodeURIComponent(code)}/price-parity/rules`, payload);
export const deleteParityRule = (code, id) => request("delete", `/properties/${encodeURIComponent(code)}/price-parity/rules/${id}`);
export const getParityViolations = (code, status) => request("get", `/properties/${encodeURIComponent(code)}/price-parity/violations`, null, { status, per_page: 100 });
export const updateParityViolation = (code, id, status) => request("patch", `/properties/${encodeURIComponent(code)}/price-parity/violations/${id}`, { status });
export const scanParity = (code, snapshotId) => request("post", `/properties/${encodeURIComponent(code)}/price-parity/scan`, snapshotId ? { snapshot_id: snapshotId } : {});
export const getParityOperators = (code) => request("get", `/properties/${encodeURIComponent(code)}/price-parity/operators`);
export const createParityOperator = (code, payload) => request("post", `/properties/${encodeURIComponent(code)}/price-parity/operators`, payload);

export const getCurrencySettings = (code) => request("get", `/properties/${encodeURIComponent(code)}/currency`);
export const getCurrencyPricing = (code, params = {}) => request("get", `/properties/${encodeURIComponent(code)}/currency/pricing`, null, params);
export const updatePropertyCurrency = (code, payload) => request("patch", `/properties/${encodeURIComponent(code)}/currency`, typeof payload === "string" ? { currency_code: payload } : payload);
export const saveCurrencyRate = (code, payload) => request("post", `/properties/${encodeURIComponent(code)}/currency/rates`, payload);
export const syncCurrencyRates = (code) => request("post", `/properties/${encodeURIComponent(code)}/currency/sync`);
export const convertCurrency = (code, payload) => request("post", `/properties/${encodeURIComponent(code)}/currency/convert`, payload);

export const getBookingWidgetConfig = (code) => request("get", `/properties/${encodeURIComponent(code)}/booking-widget`);
export const updateBookingWidgetConfig = (code, payload) => request("patch", `/properties/${encodeURIComponent(code)}/booking-widget`, payload);
export const rotateBookingWidgetKey = (code) => request("post", `/properties/${encodeURIComponent(code)}/booking-widget/rotate-key`);

export const getPmsCredentials = (code) => request("get", `/properties/${encodeURIComponent(code)}/pms-credentials`);
export const createPmsCredential = (code, payload) => request("post", `/properties/${encodeURIComponent(code)}/pms-credentials`, payload);
export const updatePmsCredential = (code, id, payload) => request("patch", `/properties/${encodeURIComponent(code)}/pms-credentials/${id}`, payload);
export const revokePmsCredential = (code, id) => request("delete", `/properties/${encodeURIComponent(code)}/pms-credentials/${id}`);
export const rotatePmsCredential = (code, id, payload) => request("post", `/properties/${encodeURIComponent(code)}/pms-credentials/${id}/rotate`, payload || {});

